import React, { useEffect, useState } from "react";
import api from "../api";
import "../Styles/GeneralJournal.css";
import { useNavigate } from "react-router-dom";

import {
  BookOpen,
  ArrowLeft,
  CalendarDays,
  Landmark,
  Scale,
  ChevronRight,
  Wallet,
} from "lucide-react";

/* GENERAL JOURNAL ACCOUNTS */

const GENERAL_JOURNAL_ACCOUNTS = [
  11,
  16,
  19,
  20,
  21,
  24,
];

const GeneralJournal = () => {

  const [entries, setEntries] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const navigate = useNavigate();

  const now = new Date();

  const [selectedMonth, setSelectedMonth] =
    useState(now.getMonth());

  const [selectedYear, setSelectedYear] =
    useState(now.getFullYear());

  useEffect(() => {

    const loadJournal = async () => {

      try {

        setLoading(true);

        const allTxns = [];

        const accounts =
          await api.getAccounts();

        for (const acc of accounts) {

          const txns =
            await api.getTransactions(acc.id);

          for (const txn of txns) {

            allTxns.push({
              ...txn,
              accountId: txn.account_id,
              accountName: acc.name,
            });

          }

        }

        const grouped = {};

        for (const txn of allTxns) {

          if (!txn.reference_id) continue;

          if (!grouped[txn.reference_id]) {
            grouped[txn.reference_id] = [];
          }

          grouped[txn.reference_id].push(txn);

        }

        const generalEntries = [];

        for (const [refId, txns] of Object.entries(grouped)) {

          const isGeneralEntry =
            txns.some(t =>
              GENERAL_JOURNAL_ACCOUNTS.includes(
                Number(t.accountId)
              )
            );

          if (!isGeneralEntry) continue;

          txns.forEach((txn) => {

            const txnDate =
              new Date(txn.date);

            const txnMonth =
              txnDate.getMonth();

            const txnYear =
              txnDate.getFullYear();

            if (
              txnMonth === selectedMonth &&
              txnYear === selectedYear
            ) {

              generalEntries.push({

                date: txn.date,

                details: txn.details,

                account:
                  txn.accountName,

                debit:
                  txn.debit || 0,

                credit:
                  txn.credit || 0,

              });

            }

          });

        }

        generalEntries.sort(
          (a, b) =>
            new Date(a.date) -
            new Date(b.date)
        );

        setEntries(generalEntries);

      } catch (err) {

        console.error(
          "Failed to load general journal:",
          err
        );

      } finally {

        setLoading(false);

      }

    };

    loadJournal();

  }, [selectedMonth, selectedYear]);

  const totalDebits =
    entries.reduce(
      (sum, e) =>
        sum + parseFloat(e.debit || 0),
      0
    );

  const totalCredits =
    entries.reduce(
      (sum, e) =>
        sum + parseFloat(e.credit || 0),
      0
    );

  if (loading) {

    return (

      <div className="general-page">

        <div className="general-loading-card">

          <div className="general-spinner"></div>

          <p>
            Loading General Journal...
          </p>

        </div>

      </div>

    );

  }

  return (

    <div className="general-page">

      {/* HERO */}

      <div className="general-hero">

        <div className="general-hero-left">

          <div className="general-pill">

            <BookOpen size={15} />

            <span>
              General Journal
            </span>

          </div>

          <h1>
            General
            <br />
            Entries
          </h1>

          <p>
            Review adjusting entries,
            liabilities, opening balances,
            equity adjustments and
            miscellaneous accounting
            transactions across the system.
          </p>

          <div className="general-actions">

            <button
              className="general-btn primary"
              onClick={() =>
                navigate("/journals")
              }
            >
              Open Journals
              <ChevronRight size={17} />
            </button>

            <button
              className="general-btn secondary"
              onClick={() =>
                navigate("/accounts")
              }
            >
              <ArrowLeft size={16} />
              Accounts
            </button>

          </div>

        </div>

        {/* RIGHT */}

        <div className="general-summary-grid">

          <div className="summary-card purple">

            <div className="summary-icon">
              <Wallet size={28} />
            </div>

            <div>

              <span>Total Debits</span>

              <h2>
                $
                {totalDebits.toLocaleString()}
              </h2>

            </div>

          </div>

          <div className="summary-card red">

            <div className="summary-icon">
              <Scale size={28} />
            </div>

            <div>

              <span>Total Credits</span>

              <h2>
                $
                {totalCredits.toLocaleString()}
              </h2>

            </div>

          </div>

          <div className="summary-card teal">

            <div className="summary-icon">
              <Landmark size={28} />
            </div>

            <div>

              <span>Entries</span>

              <h2>
                {entries.length}
              </h2>

            </div>

          </div>

          <div className="summary-card blue">

            <div className="summary-icon">
              <CalendarDays size={28} />
            </div>

            <div>

              <span>Selected Period</span>

              <h2>
                {new Date(
                  selectedYear,
                  selectedMonth
                ).toLocaleString(
                  "en-US",
                  {
                    month: "long",
                    year: "numeric",
                  }
                )}
              </h2>

            </div>

          </div>

        </div>

      </div>

      {/* FILTERS */}

      <div className="general-filter-bar">

        <div className="filter-group">

          <label>Month</label>

          <select
            value={selectedMonth}
            onChange={(e) =>
              setSelectedMonth(
                parseInt(e.target.value)
              )
            }
          >

            {Array.from(
              { length: 12 },
              (_, i) => (

                <option
                  key={i}
                  value={i}
                >
                  {new Date(
                    0,
                    i
                  ).toLocaleString(
                    "en-US",
                    {
                      month: "long",
                    }
                  )}
                </option>

              )
            )}

          </select>

        </div>

        <div className="filter-group">

          <label>Year</label>

          <select
            value={selectedYear}
            onChange={(e) =>
              setSelectedYear(
                parseInt(e.target.value)
              )
            }
          >

            {Array.from(
              { length: 5 },
              (_, i) => {

                const year =
                  now.getFullYear() - 2 + i;

                return (

                  <option
                    key={year}
                    value={year}
                  >
                    {year}
                  </option>

                );

              }
            )}

          </select>

        </div>

      </div>

      {/* TABLE */}

      <div className="general-table-wrapper">

        <table className="general-table">

          <thead>

            <tr>
              <th>Date</th>
              <th>Particulars</th>
              <th>Account</th>
              <th>Debit</th>
              <th>Credit</th>
            </tr>

          </thead>

          <tbody>

            {entries.map((e, i) => (

              <tr key={i}>

                <td>
                  {e.date.split("T")[0]}
                </td>

                <td>
                  {e.details}
                </td>

                <td>
                  {e.account}
                </td>

                <td className="debit-amount">
                  {e.debit
                    ? `$${parseFloat(
                        e.debit
                      ).toLocaleString()}`
                    : "-"}
                </td>

                <td className="credit-amount">
                  {e.credit
                    ? `$${parseFloat(
                        e.credit
                      ).toLocaleString()}`
                    : "-"}
                </td>

              </tr>

            ))}

            <tr className="total-row">

              <td colSpan="3">
                Totals
              </td>

              <td>
                $
                {totalDebits.toLocaleString()}
              </td>

              <td>
                $
                {totalCredits.toLocaleString()}
              </td>

            </tr>

          </tbody>

        </table>

      </div>

    </div>

  );

};

export default GeneralJournal;